import React, { FC } from 'react';
import { Box, useBreakpointValue, useTheme } from '@chakra-ui/react';
import { motion } from 'motion/react';
import Button from 'ts/common/components/gallery/Button';
import CoverPhoto from 'ts/common/components/gallery/CoverPhoto';
import GalleryHeading from 'ts/common/components/gallery/GalleryHeading';
import StudioHeading from 'ts/common/components/gallery/StudioHeading';
import { ILayoutProps } from './BaseLayout';
import { getAnimationProps, simpleRightAnimations } from './animations';

const SimpleRightLayout: FC<ILayoutProps> = ({
    galleryTitle,
    brandName,
    coverPhoto,
    coverPhotoUrl,
    buttonText,
    buttonTextLarge,
    colorScheme,
    onClick,
    enableAnimations = true
}) => {
    const theme = useTheme();
    const galleryHeadingSize =
        useBreakpointValue({ base: 'lg' as const, lg: 'md' as const }) ?? 'lg';

    const gradientColors =
        colorScheme === 'dark'
            ? `${theme.colors.whiteAlpha[700]} 0%, ${theme.colors.whiteAlpha[500]} 50%, rgba(255,255,255,0) 100%`
            : `${theme.colors.blackAlpha[700]} 0%, ${theme.colors.blackAlpha[500]} 50%, rgba(0,0,0,0) 100%`;

    return (
        <Box position="absolute" width="full" height="full" onClick={onClick}>
            {/* Cover image - full background */}
            <motion.div
                style={{
                    position: 'absolute',
                    width: '100%',
                    height: '100%',
                    inset: 0
                }}
                {...getAnimationProps(simpleRightAnimations.coverImage, enableAnimations)}
            >
                <CoverPhoto coverPhoto={coverPhoto} coverPhotoUrl={coverPhotoUrl} />
            </motion.div>

            {/* Gradient overlay at bottom */}
            <motion.div
                style={{
                    position: 'absolute',
                    bottom: 0,
                    left: '0',
                    width: '100%',
                    height: '50%',
                    background: `linear-gradient(to top, ${gradientColors})`
                }}
                {...(simpleRightAnimations.gradient &&
                    getAnimationProps(simpleRightAnimations.gradient, enableAnimations))}
            />

            {/* Content positioned at bottom right - responsive */}
            <Box
                position="absolute"
                right={useBreakpointValue({ base: '24px', lg: '39px' }) || '24px'}
                bottom={useBreakpointValue({ base: '90px', lg: '63px' }) || '90px'}
                width={useBreakpointValue({ base: '305px', lg: 'auto' }) || '305px'}
                textAlign="right"
                color={colorScheme === 'dark' ? 'black' : 'white'}
            >
                {/* Text Content Container */}
                <Box marginBottom={useBreakpointValue({ base: '60px', lg: '0' }) || '0'}>
                    {/* Studio Name/Logo */}
                    <motion.div
                        style={{
                            marginBottom: useBreakpointValue({ base: '32px', lg: '16px' }) || '32px'
                        }}
                        {...getAnimationProps(simpleRightAnimations.studioName, enableAnimations)}
                    >
                        <StudioHeading text={brandName} variant={colorScheme} />
                    </motion.div>

                    {/* Gallery Name */}
                    <motion.div
                        style={{
                            marginBottom: useBreakpointValue({ base: '0', lg: '32px' }) || '0'
                        }}
                        {...getAnimationProps(simpleRightAnimations.galleryName, enableAnimations)}
                    >
                        <GalleryHeading
                            size={galleryHeadingSize}
                            text={galleryTitle}
                            variant={colorScheme}
                        />
                    </motion.div>
                </Box>

                {/* Button */}
                <motion.div {...getAnimationProps(simpleRightAnimations.viewBtn, enableAnimations)}>
                    <Box
                        width={
                            useBreakpointValue({ base: 'fit-content', lg: 'auto' }) ?? 'fit-content'
                        }
                        maxWidth={useBreakpointValue({ base: '140px', lg: 'none' }) ?? '140px'}
                        marginLeft={useBreakpointValue({ base: 'auto', lg: '0' }) ?? 'auto'}
                    >
                        <Button
                            text={buttonText}
                            textLarge={buttonTextLarge}
                            variant={`secondary ${colorScheme}` as const}
                        />
                    </Box>
                </motion.div>
            </Box>
        </Box>
    );
};

export default SimpleRightLayout;
